<template>
  <div class="modal" :class="{'is-active': opened}">
    <div class="modal-background"></div>
    <div class="modal-card my-modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">Upload Landed Costs</p>
        <button class="delete" @click="close"></button>
      </header>
      <section class="modal-card-body">

        <div class="field">
          <label class="label">Cost type</label>
          <div class="control">
              <div class="select">
                <select v-model="costType">
                  <option>Freight costs</option>
                  <option>Duty costs</option>
                </select>
              </div>
          </div>
        </div>

        <div class="field">
          <label class="label">Excel file</label>
          <div class="control">
            <div class="file has-name">
              <label class="file-label">
                <input class="file-input" id="myFileInput" type="file" accept=".xlsx,.xls" @change="onExcelFileChange" />
                <span class="file-cta">
                  <span class="file-icon">
                    <i class="fas fa-upload"></i>
                  </span>
                </span>
                <span class="file-name"> {{excelFile ? excelFile.name : 'Choose a EXCEL file...'}} </span>
              </label>
            </div>
          </div>
        </div>

        <div class="field" v-if="excelFile && records.length">
          <label class="label">Preview</label>
          <div class="control" v-if="costType == 'Freight costs'">
            <table class="table is-fullwidth">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Id</th>
                  <th>MBL</th>
                  <th>Freight vendor</th>
                  <th>Freight invoice</th>
                  <th>Invoice date</th>
                  <th>Entry number</th>
                  <th>Freight cost</th>
                  <th>Freight bill id</th>
                  <th>Upload result</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, j) in records" :key="'r-r-'+j">
                  <td>{{j+1}}</td>
                  <td>{{r.inbound.id}}</td>
                  <td>{{r.inbound.mbl}}</td>
                  <td>{{r.vendor}}</td>
                  <td>{{r.invoice}}</td>
                  <td>{{r.date}}</td>
                  <td>{{r.entry}}</td>
                  <td>{{formatPrice(r.cost)}}</td>
                  <td>{{r.billId}}</td>
                  <td>
                    <span class="tag" :class="{'is-warning': r.uploadResult.startsWith('Uploading'), 'is-success': r.uploadResult.startsWith('Succeeded'), 'is-danger': r.uploadResult.startsWith('Failed')}">
                      {{r.uploadResult}}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="control" v-if="costType == 'Duty costs'">
            <table class="table is-fullwidth">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Id</th>
                  <th>Duty entry number</th>
                  <th>Duty date</th>
                  <th>Duty cost</th>
                  <th>Duty bill id</th>
                  <th>Upload result</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, j) in records" :key="'r-r-'+j">
                  <td>{{j+1}}</td>
                  <td>{{r.inbound.id}}</td>
                  <td>{{r.inbound.landedCost.dutyEntryNum}}</td>
                  <td>{{r.date}}</td>
                  <td>{{formatPrice(r.cost)}}</td>
                  <td>{{r.billId}}</td>
                  <td>
                    <span class="tag" :class="{'is-warning': r.uploadResult.startsWith('Uploading'), 'is-success': r.uploadResult.startsWith('Succeeded'), 'is-danger': r.uploadResult.startsWith('Failed')}">
                      {{r.uploadResult}}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        
        <div v-if="invalidRecords.length" class="notification is-warning is-light">
          <p><strong>Removed rows:</strong></p>
          <p v-for="(ir, j) in invalidRecords" :key="'i-r-' + j">{{ ir }}</p>
        </div>

        <div v-if="error" class="notification is-danger is-light">
          <button class="delete" @click="error=''"></button>
          {{error}}
        </div>
      </section>
      <footer class="modal-card-foot">
        <a class="button is-link" :class="{'is-loading': waiting}" :disabled="!valid" @click="uploadLandedCosts">Upload landed costs</a>
        <a class="button" @click="close">Close</a>
      </footer>
    </div>
  </div>
</template>
  
<script>
import Excel from 'exceljs'

export default {
  name: 'upload-landed-costs-modal',
  props: ['opened', 'inbounds'],
  data () {
    return {
      error: '',
      waiting: false,
      costType: 'Freight costs',
      excelFile: null,
      records: [],
      invalidRecords: [],
      landedCostCommon: {
        expenseAccountNsId: '259',
        subsidiaryName: '',
        freightVendorName: '',
        freightAccountNsId: '6102',
        freightInvoiceNum: '',
        freightCost: null,
        freightBillNsId: '',
        freightInvoiceDate: '',
        dutyVendorName: '',
        dutyAccountNsId: '',
        dutyEntryNum: '',
        dutyCost: null,
        dutyBillNsId: '',
        dutyInvoiceDate: '',
      },

    }
  },
  computed: {
    server () {
      return this.$store.state.config.server
    },
    valid () {
      return this.records.length
    },
    mblMap () {
      var mblMap = {}
      for (var i of this.inbounds) {
        if (i.shipmentStatus != 'closed' && i.shipmentNumber && i.mbl) {
          mblMap[i.mbl] = i
        }
      }
      return mblMap
    },
    entryNumMap () {
      var entryNumMap = {}
      for (var i of this.inbounds) {
        if (i.shipmentStatus != 'closed' && i.shipmentNumber && i.landedCost && i.landedCost.dutyEntryNum) {
          entryNumMap[i.landedCost.dutyEntryNum.replaceAll('-', '').trim()] = i
        }
      }
      return entryNumMap
    },
    vendorList () {
      return this.$store.state.config.vendorList
    },
    vendorMap () {
      var vendorMap = {}
      for (var vendor of this.vendorList) {
        var ss = vendor.split(' ')
        var name = ss.slice(1).join(' ')
        vendorMap[name] = vendor
      }
      return vendorMap
    },
  },
  watch: {
    opened: function (val) {
      if (val && this.inbounds) {
        this.reset()
      }
    },
    costType: function (val) {
      this.reset()
    },
  },
  methods: {
    findVendor (vendorName) {
      if (this.vendorMap[vendorName]) {
        return this.vendorMap[vendorName]
      }
      for (var name of Object.keys(this.vendorMap)) {
        if (name.toLowerCase().startsWith(vendorName.toLowerCase())) {
          return this.vendorMap[name]
        }
      }
      for (var name of Object.keys(this.vendorMap)) {
        if (name.toLowerCase().includes(vendorName.toLowerCase())) {
          return this.vendorMap[name]
        }
      }
      return null
    },
    reset () {
      this.error = ''
      this.waiting = false
      this.excelFile = null
      this.records = []
      this.invalidRecords = []
    },
    close () {
      this.$emit('closeUploadLandedCostsModal')
    },
    onExcelFileChange (e) {
      this.reset()
      var files = e.target.files || e.dataTransfer.files;
      if (!files.length)
        return;
      this.excelFile = files[0];
      var reader = new FileReader()
      var vm = this
      reader.onload = async function () {
        var data = reader.result
        const workbook = new Excel.Workbook();
        await workbook.xlsx.load(data)
        var worksheet = workbook.worksheets[0]
        if (workbook.worksheets.length > 1 && vm.costType == 'Freight costs') {
          for (var sheet of workbook.worksheets) {
            if (sheet.name == 'Main Report') {
              worksheet = sheet
              break
            }
          }
        }
        var records = []
        if (vm.costType == 'Freight costs') {
          records = vm.getFreightRecords(worksheet)
        } else {
          records = vm.getDutyRecords(worksheet)
        }
        vm.records = records
        console.log('records1', records)
      }
      reader.readAsArrayBuffer(this.excelFile)
    },
    getFreightRecords (worksheet) {
      var vm = this
      var records = []
      var invalidRecords = []
      var startRow = 2
      var vendorCol = vm.getColNum('Vendor name', worksheet)
      var invoiceCol = vm.getColNum('Invoice', worksheet)
      var dateCol = vm.getColNum('Invoice date', worksheet)
      var mblCol = vm.getColNum('MBL', worksheet)
      var entryCol = vm.getColNum('Entry number', worksheet)
      var costCol = vm.getColNum('Total freight cost amount', worksheet)

      for (var i=startRow;i<=worksheet.actualRowCount;i++) {
        var row = worksheet.getRow(i)
        if (!row.getCell(mblCol).value) {
          invalidRecords.push({row: i, reason: 'MBL missing'})
          continue
        }
        var mbl = row.getCell(mblCol).value.toString().trim()
        var inbound = vm.mblMap[mbl]
        if (!inbound) {
          invalidRecords.push({row: i, reason: 'Cannot find inbound by MBL: ' + mbl})
          continue
        }
        var vendorName = row.getCell(vendorCol).value.toString().trim()
        var vendor = vm.findVendor(vendorName)
        if (!vendor) {
          invalidRecords.push({row: i, reason: 'Cannot find vendor by vendor name: ' + vendorName})
          continue
        }
        var entry = row.getCell(entryCol).value
        if (!entry) {
          invalidRecords.push({row: i, reason: 'Entry number missing'})
          continue
        }
        entry = entry.toString().replaceAll('-', '').trim()

        var cost = row.getCell(costCol).value
        if (!cost) {
          invalidRecords.push({row: i, reason: 'Cannot find freight cost'})
          continue
        }
        var invoice = row.getCell(invoiceCol).value
        if (!invoice) {
          invalidRecords.push({row: i, reason: 'Cannot find freight invoice'})
          continue
        }
        invoice = invoice.toString().trim()

        var date = row.getCell(dateCol).value
        if (!date) {
          invalidRecords.push({row: i, reason: 'Cannot find invoice date'})
          continue
        }
        date = date.toISOString().split('T')[0]

        records.push({
          vendor: vendor,
          invoice: invoice,
          date: date,
          mbl: mbl,
          entry: entry,
          cost: cost,
          billId: (inbound.landedCost && inbound.landedCost.freightBillNsId) ? inbound.landedCost.freightBillNsId : '',
          inbound: JSON.parse(JSON.stringify(inbound)),
          uploadResult: 'Pending ' + ((inbound.landedCost && inbound.landedCost.freightBillNsId) ? 'update' : 'creation'),
        })
      }
      vm.invalidRecords = invalidRecords
      return records
    },
    getDutyRecords (worksheet) {
      var vm = this
      var records = []
      var invalidRecords = []
      var startRow = 2
      var entryNumCol = vm.getColNum('Entry Summary Number', worksheet)
      var costCol = vm.getColNum('Grand Total Amount', worksheet)
      var dateCol = vm.getColNum('Daily Statement Payment Authorized Date', worksheet)
      for (var i=startRow;i<=worksheet.actualRowCount;i++) {
        var row = worksheet.getRow(i)
        if (!row.getCell(entryNumCol).value) {
          //invalidRecords.push({row: i, reason: 'Duty entry number missing'})
          continue
        }
        var entryNum = row.getCell(entryNumCol).value.toString().replaceAll('-', '').trim()
        var inbound = vm.entryNumMap[entryNum]
        if (!inbound) {
          invalidRecords.push({row: i, reason: 'Cannot find inbound by Duty entry number: ' + entryNum})
          continue
        }
        if (!row.getCell(costCol).value) {
          invalidRecords.push({row: i, reason: 'Cannot find the cost of ' + entryNum})
          continue
        }
        if (!row.getCell(dateCol).value) {
          invalidRecords.push({row: i, reason: 'Cannot find the date of ' + entryNum})
          continue
        }
        var cost = row.getCell(costCol).value
        var date = row.getCell(dateCol).value.toISOString().split('T')[0]
        records.push({
          date: date,
          entryNum: entryNum,
          cost: cost,
          billId: (inbound.landedCost && inbound.landedCost.dutyBillNsId) ? inbound.landedCost.dutyBillNsId : '',
          inbound: JSON.parse(JSON.stringify(inbound)),
          uploadResult: 'Pending ' + ((inbound.landedCost && inbound.landedCost.dutyBillNsId) ? 'update' : 'creation'),
        })
      }
      vm.invalidRecords = invalidRecords
      return records
    },
    getColNum (col, worksheet) {
      if (typeof(col) != 'string') {
        return col
      }

      for (var i=1;i<=worksheet.actualRowCount;i++) {
        var row = worksheet.getRow(i)
        var colNum = null
        row.eachCell(function(cell, colNumber) {
          if (cell.value && cell.value.toString().toLowerCase().replaceAll(' ', '') == col.toLowerCase().replaceAll(' ', '')) {
            colNum = colNumber
          }
        });
        if (colNum) {
          return colNum
        }
      }
    },
    uploadLandedCosts () {
      if (!this.valid) {
        return
      }
      if (this.costType == 'Freight costs') {
        this.uploadFreights(0)
      } else {
        this.uploadDuties(0)
      }
    },
    uploadFreights (index) {
      this.waiting = true
      var record = this.records[index]
      var inbound = record.inbound
      var isCanada = inbound.locationName.includes('Ontario')
      var newLandedCost = {
        ...this.landedCostCommon,
        ...inbound.landedCost,
        subsidiaryName: isCanada ? 'VANITY ART INC' : 'VANITY ART LLC',
        dutyVendorName: isCanada ? 'V0432 Canada Border Services Agency' : 'V0901 U.S. Customs & Border Protection',
        dutyAccountNsId: isCanada ? '214' : '1262',
        dutyEntryNum: record.entry,
        freightVendorName: record.vendor,
        freightInvoiceNum: record.invoice,
        freightCost: record.cost,
        freightInvoiceDate: record.date,
      }
      var message = {
        inboundId: inbound.id,
        landedCost: newLandedCost,
        submitFreightCost: true,
      }
      this.records[index].uploadResult = 'Uploading...'
      this.$http.post(this.server + '/myapp/save-inbound-landed-cost/', message).then(resp => {
        this.records[index].uploadResult = 'Succeeded'
        this.records[index].freightBillNsId = resp.body.freightBillNsId
        if (index + 1 < this.records.length) {
          this.uploadFreights(index + 1)
        } else {
          this.waiting = false
          this.$emit('inboundsUpdated')
        }
      }, err => {
        this.records[index].uploadResult = 'Failed'
        if (index + 1 < this.records.length) {
          this.uploadFreights(index + 1)
        } else {
          this.waiting = false
          this.$emit('inboundsUpdated')
        }
      })
    },
    uploadDuties (index) {
      this.waiting = true
      var record = this.records[index]
      var inbound = record.inbound
      var isCanada = inbound.locationName.includes('Ontario')
      var newLandedCost = {
        ...this.landedCostCommon,
        ...inbound.landedCost,
        subsidiaryName: isCanada ? 'VANITY ART INC' : 'VANITY ART LLC',
        dutyVendorName: isCanada ? 'V0432 Canada Border Services Agency' : 'V0901 U.S. Customs & Border Protection',
        dutyAccountNsId: isCanada ? '214' : '1262',
        dutyEntryNum: record.entryNum,
        dutyCost: record.cost,
        dutyInvoiceDate: record.date,
      }
      var message = {
        inboundId: inbound.id,
        landedCost: newLandedCost,
        submitDutyCost: true,
      }
      this.records[index].uploadResult = 'Uploading...'
      this.$http.post(this.server + '/myapp/save-inbound-landed-cost/', message).then(resp => {
        this.records[index].uploadResult = 'Succeeded'
        this.records[index].dutyBillNsId = resp.body.dutyBillNsId
        if (index + 1 < this.records.length) {
          this.uploadDuties(index + 1)
        } else {
          this.waiting = false
          this.$emit('inboundsUpdated')
        }
      }, err => {
        this.records[index].uploadResult = 'Failed'
        if (index + 1 < this.records.length) {
          this.uploadDuties(index + 1)
        } else {
          this.waiting = false
          this.$emit('inboundsUpdated')
        }
      })
    },
    formatPrice (value) {
      if (!value) {
        return ''
      }
      return '$' + value.toLocaleString('en-US', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
        useGrouping: false
      })
    },
  },
}
</script>

<style scoped>
.my-modal-card {
  width: 80%!important;
}

</style>